import { forwardRef, type HTMLAttributes } from 'react';

import { cn } from './utils';

export type BadgeTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'violet';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  dot?: boolean;
  size?: 'sm' | 'md';
}

const toneClasses: Record<BadgeTone, string> = {
  neutral: 'bg-slate-100 text-slate-600 ring-slate-200/80',
  info: 'bg-sky-50 text-sky-700 ring-sky-200/80',
  success: 'bg-emerald-50 text-emerald-700 ring-emerald-200/80',
  warning: 'bg-amber-50 text-amber-800 ring-amber-200/80',
  danger: 'bg-rose-50 text-rose-700 ring-rose-200/80',
  violet: 'bg-violet-50 text-violet-700 ring-violet-200/80',
};

const dotClasses: Record<BadgeTone, string> = {
  neutral: 'bg-slate-400',
  info: 'bg-sky-500',
  success: 'bg-emerald-500',
  warning: 'bg-amber-500',
  danger: 'bg-rose-500',
  violet: 'bg-violet-500',
};

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { children, className, dot = false, size = 'sm', tone = 'neutral', ...props },
  ref,
) {
  return (
    <span
      {...props}
      ref={ref}
      className={cn(
        'inline-flex w-fit items-center whitespace-nowrap rounded-full font-semibold ring-1 ring-inset',
        size === 'sm'
          ? 'gap-1.5 px-2 py-0.5 text-xs leading-4'
          : 'gap-2 px-2.5 py-1 text-sm leading-4',
        toneClasses[tone],
        className,
      )}
    >
      {dot ? (
        <span aria-hidden="true" className={cn('size-1.5 rounded-full', dotClasses[tone])} />
      ) : null}
      {children}
    </span>
  );
});

Badge.displayName = 'Badge';
